import { useCallback, useEffect, useMemo, useState } from "react";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { IPagedReviewsModel, IStudioReviewModel } from "../models/reviewModels";
import { createReviewsSeed } from "../utils/reviewUtility";

export const REVIEWS_PAGE_SIZE = 10;

/**
 * The "Ревюта" dialog: the studio's visible reviews in a shuffled order, ten at a time.
 * Each opening picks a new seed, so the order differs every time, while "Покажи още"
 * appends the next page of the same order; reopening starts from the first one again.
 */
export const useReviewsDialog = (studioId: number | undefined, isOpen: boolean) => {
  const [page, setPage] = useState(1);
  const [loadedPages, setLoadedPages] = useState<
    Record<number, IStudioReviewModel[]>
  >({});
  const [knownTotalCount, setKnownTotalCount] = useState(0);
  const [seed, setSeed] = useState(createReviewsSeed);

  const { data, isLoading, isFetching } = useApiQuery<IPagedReviewsModel>({
    queryKey: [endpointsMetadata.publicReviews.getPage, studioId, seed, page],
    endpoint: `${endpointsMetadata.publicReviews.getPage}?studioId=${studioId}&page=${page}&pageSize=${REVIEWS_PAGE_SIZE}&seed=${seed}`,
    enabled: isOpen && !!studioId,
    globalIsLoading: false,
    retry: false,
  });

  useEffect(() => {
    if (!isOpen) {
      setPage(1);
      setLoadedPages({});
      setKnownTotalCount(0);
      setSeed(createReviewsSeed());
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && data) {
      setLoadedPages((pages) => ({ ...pages, [page]: data.items }));
      setKnownTotalCount(data.totalCount);
    }
  }, [isOpen, data, page]);

  const items = useMemo(() => {
    if (!isOpen) {
      return [];
    }

    const result: IStudioReviewModel[] = [];
    for (let p = 1; p <= page; p++) {
      result.push(...((p === page ? data?.items : undefined) ?? loadedPages[p] ?? []));
    }
    return result;
  }, [isOpen, page, data, loadedPages]);

  // Kept while the next page loads, so the count and "Покажи още" do not blink.
  const totalCount = data?.totalCount ?? knownTotalCount;
  const hasMore = items.length > 0 && items.length < totalCount;

  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setPage((current) => current + 1);
    }
  }, [hasMore, isFetching]);

  return {
    items,
    totalCount,
    hasMore,
    isLoading: isLoading || isFetching,
    loadMore,
  };
};
